import { useEffect, useMemo, useRef, useState } from "react";
import {
  WEATHER_SCHEDULE_INTERVALS,
  fetchWeatherSourceHealth,
  pollWeatherRefreshUntilComplete,
  runWeatherSource,
  updateWeatherSource,
} from "../../../../services/weatherSourceControls";
import WeatherHealthHistoryModal from "./WeatherHealthHistoryModal";
import DataSourceControlsGrid from "../dataSources/DataSourceControlsGrid";
import DataSourceScheduleModal from "../dataSources/DataSourceScheduleModal";
import { mapWeatherSourceToGridRow } from "../dataSources/mapDataSourceGridRow";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";

function normalizeHealth(source) {
  if (!source.isActive) {
    return "disabled";
  }

  if (source.isRunning || source.health === "running" || source.lastStatus === "running") {
    return "running";
  }

  if (source.health === "failed" || source.lastStatus === "failed") {
    return "failed";
  }

  if (source.health === "healthy") {
    return "healthy";
  }

  if (source.health === "warning") {
    return "warning";
  }

  return source.health || (source.latestHealth ? "warning" : "never_run");
}

const WeatherSourceControlsTable = ({
  sources,
  filters,
  onRefresh,
  showToast,
  setSources,
  hideEmpty = false,
}) => {
  const [busyCode, setBusyCode] = useState(null);
  const [scheduleSource, setScheduleSource] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [healthSource, setHealthSource] = useState(null);
  const [healthRecords, setHealthRecords] = useState([]);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState("");
  const pollAbortRef = useRef(null);
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageSources =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.WEATHER_LAYERS_MANAGE);

  useEffect(() => {
    return () => {
      pollAbortRef.current?.abort();
    };
  }, []);

  const filteredSources = useMemo(() => {
    let list = [...(sources || [])];

    if (filters.search) {
      const query = filters.search.toLowerCase();
      list = list.filter(
        (source) =>
          source.sourceName?.toLowerCase().includes(query) ||
          source.sourceCode?.toLowerCase().includes(query) ||
          source.sourceCategory?.toLowerCase().includes(query)
      );
    }

    if (filters.enabledFilter === "enabled") {
      list = list.filter((source) => source.isActive);
    } else if (filters.enabledFilter === "disabled") {
      list = list.filter((source) => !source.isActive);
    }

    if (filters.statusFilter && filters.statusFilter !== "all") {
      list = list.filter((source) => normalizeHealth(source) === filters.statusFilter);
    }

    return list
      .sort((a, b) => a.sourceName.localeCompare(b.sourceName))
      .map((source) => mapWeatherSourceToGridRow(source));
  }, [sources, filters]);

  const patchSourceInList = (updated) => {
    if (!updated?.sourceCode) {
      return;
    }

    setSources((current) =>
      current.map((item) =>
        item.sourceCode === updated.sourceCode ? { ...item, ...updated } : item
      )
    );
  };

  const handleToggleActive = async (source) => {
    const nextActive = !source.isActive;
    setBusyCode(source.sourceCode);

    setSources((current) =>
      current.map((item) =>
        item.sourceCode === source.sourceCode
          ? {
              ...item,
              isActive: nextActive,
              health: nextActive ? item.health : "disabled",
            }
          : item
      )
    );

    try {
      const updated = await updateWeatherSource(source.sourceCode, { isActive: nextActive });
      patchSourceInList(updated);
      showToast(
        nextActive ? `${source.sourceName} enabled.` : `${source.sourceName} disabled.`,
        "success"
      );
      onRefresh?.({ silent: true });
    } catch (error) {
      setSources((current) =>
        current.map((item) =>
          item.sourceCode === source.sourceCode ? { ...item, isActive: source.isActive } : item
        )
      );
      showToast(error.message || "Unable to update source.", "error");
    } finally {
      setBusyCode(null);
    }
  };

  const handleRunNow = async (source) => {
    setBusyCode(source.sourceCode);
    pollAbortRef.current?.abort();
    pollAbortRef.current = new AbortController();

    try {
      const result = await runWeatherSource(source.sourceCode);

      showToast(
        result.message || `Refresh started for ${source.sourceName}.`,
        "success"
      );

      setSources((current) =>
        current.map((item) =>
          item.sourceCode === source.sourceCode
            ? {
                ...item,
                isRunning: true,
                health: "running",
                lastStatus: result.status || "RUNNING",
              }
            : item
        )
      );

      const finalSource = await pollWeatherRefreshUntilComplete(source.sourceCode, {
        signal: pollAbortRef.current.signal,
        onUpdate: (updated) => {
          patchSourceInList(updated);
        },
      });

      const tone = finalSource.lastStatus === "failed" ? "error" : "success";
      showToast(
        finalSource.lastStatus === "failed"
          ? `Refresh failed for ${source.sourceName}.`
          : `Refresh completed for ${source.sourceName}.`,
        tone
      );

      onRefresh?.({ silent: true });
    } catch (error) {
      showToast(error.message || `Unable to start refresh for ${source.sourceName}.`, "error");
      onRefresh?.({ silent: true });
    } finally {
      setBusyCode(null);
    }
  };

  const handleSaveSchedule = async (source, payload) => {
    setScheduleSaving(true);

    try {
      const updated = await updateWeatherSource(source.sourceCode, payload);
      patchSourceInList(updated);
      setScheduleSource(null);
      showToast(`Schedule updated for ${source.sourceName}.`, "success");
      onRefresh?.({ silent: true });
    } catch (error) {
      showToast(error.message || "Unable to save schedule.", "error");
      throw error;
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleViewHealth = async (source) => {
    setHealthSource(source);
    setHealthLoading(true);
    setHealthError("");

    try {
      const data = await fetchWeatherSourceHealth(source.sourceCode, { limit: 25 });
      setHealthRecords(data.records || []);
    } catch (error) {
      setHealthRecords([]);
      setHealthError(error.message || "Unable to load provider health history.");
    } finally {
      setHealthLoading(false);
    }
  };

  if (!filteredSources.length) {
    if (hideEmpty) {
      return null;
    }
    return (
      <p className="muted">
        {sources?.length
          ? "No sources match the current filters."
          : "No weather sources are registered yet."}
      </p>
    );
  }

  return (
    <>
      <DataSourceControlsGrid
        sources={filteredSources}
        emptyMessage={
          sources?.length
            ? "No sources match the current filters."
            : "No weather sources are registered yet."
        }
        busyKey={busyCode}
        canManageSources={canManageSources}
        onToggleEnabled={handleToggleActive}
        onRunNow={handleRunNow}
        deleteDomain="weather"
        onDeleted={() => onRefresh?.()}
        onSaveSchedule={handleSaveSchedule}
        onViewRuns={handleViewHealth}
        sectionLabel="Weather"
        scheduleIntervals={WEATHER_SCHEDULE_INTERVALS}
        viewRunsLabel="Health history"
      />

      <DataSourceScheduleModal
        source={scheduleSource}
        isOpen={Boolean(scheduleSource)}
        isSaving={scheduleSaving}
        onClose={() => setScheduleSource(null)}
        onSave={handleSaveSchedule}
        intervals={WEATHER_SCHEDULE_INTERVALS}
      />

      <WeatherHealthHistoryModal
        source={healthSource}
        records={healthRecords}
        isOpen={Boolean(healthSource)}
        isLoading={healthLoading}
        errorMessage={healthError}
        onClose={() => {
          setHealthSource(null);
          setHealthRecords([]);
          setHealthError("");
        }}
      />
    </>
  );
};

export default WeatherSourceControlsTable;
